import { useMemo } from 'react';

import { VESTIAIRE_FULL_LIST_LIMIT, materialLevelsById } from '@/features/productSearch/vestiaire/vestiaireFilterOptions';
import { type VestiaireSearchUrl, buildVestiaireSearchUrl } from '@/features/productSearch/vestiaire/vestiaireUrl';
import type { VestiaireSearchCriteriaInput } from '@/shared/api/types/generated';
import {
  useGetVestiaireCountriesQuery,
  useGetVestiaireFilterOptionsQuery,
} from '@/shared/hooks/queries/generated';
import { useUserLanguage } from '@/shared/hooks/useUserLanguage';

// Listes pas encore chargées : leurs ids sont signalés comme absents de l'URL.
const NO_COUNTRIES: ReadonlyMap<number, string> = new Map();
const NO_MATERIALS: ReadonlyMap<number, number> = new Map();

/**
 * Codes ISO à 2 lettres des pays VC (id = ISO numérique), `null` tant que la liste n'est pas chargée.
 * Même requête et même cache que le panneau (1 h).
 */
export const useVestiaireCountryIsoCodes = (enabled: boolean): ReadonlyMap<number, string> | null => {
  const { data } = useGetVestiaireCountriesQuery(undefined, { enabled, staleTime: 60 * 60 * 1000 });
  return useMemo(
    () => (data ? new Map(data.getVestiaireCountries.map((country) => [country.id, country.isoCode])) : null),
    [data],
  );
};

/**
 * Niveau VC (0/1) de chaque matière, `null` tant que la liste n'est pas chargée. Même requête et même
 * clé de cache que la liste complète du panneau (`VESTIAIRE_FULL_LIST_LIMIT`, `staleTime: Infinity`).
 */
export const useVestiaireMaterialLevels = (enabled: boolean): ReadonlyMap<number, number> | null => {
  const { data } = useGetVestiaireFilterOptionsQuery(
    { filterType: 'material', query: null, limit: VESTIAIRE_FULL_LIST_LIMIT, ids: null },
    { enabled, staleTime: Infinity },
  );
  return useMemo(() => (data ? materialLevelsById(data.getVestiaireFilterOptions) : null), [data]);
};

/**
 * URL de recherche VC équivalente au formulaire (aperçu de SearchModal), dans la langue du compte.
 * Pays : ISO numérique → code à 2 lettres par la liste des pays VC ; matières : niveau VC par la
 * liste des matières (requêtes du panneau, déjà en cache). Tant qu'une liste se charge, ses ids
 * sont signalés comme absents de l'URL. `enabled` coupe les requêtes hors de l'onglet Vestiaire.
 */
export const useVestiaireSearchUrl = (criteria: VestiaireSearchCriteriaInput, enabled: boolean): VestiaireSearchUrl => {
  const { language } = useUserLanguage();
  const countryIsoCodes = useVestiaireCountryIsoCodes(enabled);
  const materialLevels = useVestiaireMaterialLevels(enabled);
  return useMemo(
    () => buildVestiaireSearchUrl(criteria, language, countryIsoCodes ?? NO_COUNTRIES, materialLevels ?? NO_MATERIALS),
    [criteria, language, countryIsoCodes, materialLevels],
  );
};
